import { cn } from '@iris/ui/lib/utils';
import { type ReactNode, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * ADR 0014: phone-first screens (the customer app, the cashier) keep a phone's size on a computer. From 768 px they
 * sit in a centred frame of 430 px. The frame does not scroll: through its transform it is the box of everything
 * fixed inside it (bottom bar, the counter's result screen), so those stay put at the frame's edges while the screen
 * scrolls in the inner area. Below 768 px nothing changes. Each new screen starts at the top.
 *
 * The same three elements are always rendered (`display: contents` when off), so turning the frame on once the role
 * is known never rebuilds the screen inside — a code being typed is kept.
 */
export function PhoneFrame({ enabled = true, children }: { enabled?: boolean; children: ReactNode }) {
  const scroller = useRef<HTMLDivElement>(null);
  const { pathname } = useLocation();
  useEffect(() => {
    scroller.current?.scrollTo({ top: 0 });
  }, [pathname]);
  return (
    <div
      className={cn(
        enabled
          ? 'md:flex md:h-dvh md:items-center md:justify-center md:bg-[radial-gradient(ellipse_at_top,hsl(var(--brand-50)),hsl(var(--neutral-100))_60%)] md:p-6'
          : 'contents',
      )}
    >
      <div
        className={cn(
          enabled
            ? 'md:relative md:h-[min(calc(100dvh-3rem),920px)] md:w-[430px] md:overflow-hidden md:rounded-3xl md:bg-neutral-50 md:shadow-3 md:ring-1 md:ring-black/10 md:[transform:translateZ(0)]'
            : 'contents',
        )}
      >
        <div
          ref={scroller}
          data-phone-scroller={enabled ? '' : undefined}
          className={cn(
            enabled ? 'md:h-full md:overflow-y-auto md:overflow-x-hidden md:overscroll-contain' : 'contents',
          )}
        >
          {children}
        </div>
      </div>
    </div>
  );
}
